import cx from "classnames";
import React from "react";
import type { DateValue } from "react-aria";
import { useDateField, useLocale } from "react-aria";
import type { DateRangePickerState, DateSegmentType } from "react-stately";
import { useDateFieldState } from "react-stately";
import { CalendarDate, createCalendar } from "@internationalized/date";
import type { AriaDateFieldOptions } from "@react-aria/datepicker";
import type { Props as FieldProps } from "../Form/Field";
import Field from "../Form/Field";
import { styled } from "@linaria/react";
import DateSegment from "../DatePicker/DateSegment";
import { color } from "..";
import { CheckLine, CloseCircleFill, ErrorWarningFill } from "../IconV2/react";
import { colors } from "../Theme";
import type { Preset } from "./PresetSelector";
import { Text } from "../Text";
import { css } from "@linaria/core";
import type { MappedDateValue } from "@react-types/datepicker";
import type { RangeValue } from "@react-types/shared";
import TooltipV2 from "../TooltipV2";
import t from "../i18n";
import ButtonIconV2 from "../ButtonIconV2";

export interface DateFieldProps<T extends DateValue> extends FieldProps {
  startDateOptions?: Partial<Omit<AriaDateFieldOptions<T>, "label" | "errorMessage">>;
  endDateOptions?: Partial<Omit<AriaDateFieldOptions<T>, "label" | "errorMessage">>;
  state: DateRangePickerState;
  onTemporaryChange?: (range: { start: DateValue | null; end: DateValue | null }) => void;
  applyChanges?: () => void;
  cancelChanges?: () => void;
  resetRange: () => void;
  manualValidation?: boolean;
  tempRangeValue?: RangeValue<MappedDateValue<T>> | null;
  isDateUnavailable?: ((date: DateValue, anchorDate: CalendarDate | null) => boolean) | undefined;
  wrapperRef?: React.RefObject<HTMLDivElement>;
  hasEditedWithKeyboard: boolean;
  setHasEditedWithKeyboard: React.Dispatch<React.SetStateAction<boolean>>;
  hideCalendar?: boolean;
}

function DateRangeField<T extends DateValue>({
  state,
  startDateOptions,
  endDateOptions,
  preset,
  clearPreset,
  onTemporaryChange,
  applyChanges,
  cancelChanges,
  resetRange,
  manualValidation = false,
  tempRangeValue,
  isDateUnavailable,
  wrapperRef,
  setHasEditedWithKeyboard,
  hasEditedWithKeyboard,
  hideCalendar = false,
  ...fieldProps
}: DateFieldProps<T> & {
  preset?: Preset<DateValue> | null;
  clearPreset?: () => void;
}) {
  const { locale } = useLocale();
  const [error, setError] = React.useState<string | null>(null);
  const ignoreNextBlurRef = React.useRef(false);

  const handleStartChange = React.useCallback(
    (newValue: DateValue | null) => {
      if (newValue !== null) {
        const newRange = { start: newValue, end: tempRangeValue?.end ?? null };
        onTemporaryChange?.(newRange);
      }
    },
    [onTemporaryChange, tempRangeValue?.end],
  );

  const handleEndChange = React.useCallback(
    (newValue: DateValue | null) => {
      if (newValue !== null) {
        const newRange = {
          start: tempRangeValue?.start ?? null,
          end: newValue,
        };
        onTemporaryChange?.(newRange);
      }
    },
    [onTemporaryChange, tempRangeValue?.start],
  );

  const startState = useDateFieldState({
    ...startDateOptions,
    isDisabled: hideCalendar || fieldProps.disabled,
    locale,
    createCalendar,
    ...(manualValidation
      ? {
          value: tempRangeValue?.start,
          onChange: handleStartChange,
        }
      : {}),
  });

  const endState = useDateFieldState({
    ...endDateOptions,
    isDisabled: hideCalendar || fieldProps.disabled,
    locale,
    createCalendar,
    ...(manualValidation
      ? {
          value: tempRangeValue?.end,
          onChange: handleEndChange,
        }
      : {}),
  });

  const startFieldRef = React.useRef<HTMLDivElement>(null);
  const endFieldRef = React.useRef<HTMLDivElement>(null);

  const { fieldProps: startAriaFieldProps } = useDateField(
    startDateOptions || {},
    startState,
    startFieldRef,
  );

  const { fieldProps: endAriaFieldProps } = useDateField(
    endDateOptions || {},
    endState,
    endFieldRef,
  );

  const handleClear = React.useCallback(() => {
    resetRange();
  }, [resetRange]);

  const yearsAreComplete =
    tempRangeValue?.start?.year?.toString()?.length === 4 &&
    tempRangeValue?.end?.year?.toString()?.length === 4;

  const isDateRangeAvailable = React.useMemo(() => {
    if (!tempRangeValue?.start || !tempRangeValue?.end) return false;
    if (
      manualValidation &&
      isDateUnavailable &&
      yearsAreComplete &&
      !isDateUnavailable(tempRangeValue?.start, null) &&
      !isDateUnavailable(tempRangeValue?.end, null)
    ) {
      return true;
    }
    return false;
  }, [
    tempRangeValue?.start,
    tempRangeValue?.end,
    manualValidation,
    isDateUnavailable,
    yearsAreComplete,
  ]);

  const isValidDate = React.useMemo(() => {
    if (!tempRangeValue?.start || !tempRangeValue?.end) return false;

    if (manualValidation) {
      if (!yearsAreComplete) return true;
      if (!isDateRangeAvailable) {
        return false;
      }

      return tempRangeValue?.start?.compare(tempRangeValue?.end) < 0;
    }

    if (!state.value?.start || !state.value?.end) return false;
    return state.value.start.compare(state.value.end) < 0;
  }, [
    tempRangeValue?.start,
    tempRangeValue?.end,
    manualValidation,
    state.value?.start,
    state.value?.end,
    yearsAreComplete,
    isDateRangeAvailable,
  ]);

  // Set error message
  React.useEffect(() => {
    if (!tempRangeValue?.start || !tempRangeValue?.end) {
      setError(null);
      return;
    }
    if (manualValidation) {
      if (tempRangeValue.start.compare(tempRangeValue.end) > 0) {
        setError(t("daterangepicker.error.startdate_superior_to_enddate"));
      } else if (!isDateRangeAvailable) {
        setError(t("daterangepicker.error.dates_unavailable"));
      } else if (isValidDate) {
        setError(null);
      }
    } else {
      if (isValidDate) {
        setError(null);
      } else {
        setError(t("daterangepicker.error.startdate_superior_to_enddate"));
      }
    }
  }, [
    manualValidation,
    isDateRangeAvailable,
    isValidDate,
    tempRangeValue?.start,
    tempRangeValue?.end,
  ]);

  const handleValidation = React.useCallback(() => {
    if (tempRangeValue?.start && tempRangeValue?.end && isValidDate) {
      applyChanges?.();
    }
  }, [applyChanges, isValidDate, tempRangeValue]);

  const localWrapperRef = React.useRef<HTMLDivElement>(null);
  const effectiveWrapperRef = wrapperRef || localWrapperRef;

  const [isFocused, setIsFocused] = React.useState(false);

  React.useEffect(() => {
    const controller = new AbortController();
    const handleFocusIn = () => setIsFocused(true);
    const handleFocusOut = (e: FocusEvent) => {
      const next = e.relatedTarget as HTMLElement | null;

      if (!effectiveWrapperRef.current?.contains(next)) {
        setIsFocused(false);
        setActiveSegmentKey(null);
        if (!tempRangeValue?.start && !tempRangeValue?.end) {
          return;
        }

        const isDirty =
          tempRangeValue?.start?.toString() !== state.value?.start?.toString() ||
          tempRangeValue?.end?.toString() !== state.value?.end?.toString();

        // Correction: always call cancelChanges if dirty and manualValidation, even if the field is focused after a chart zoom
        if (manualValidation && isDirty && !state.isOpen) {
          cancelChanges?.();
        }

        ignoreNextBlurRef.current = false;
      }
    };

    const wrapper = effectiveWrapperRef.current;
    if (wrapper) {
      wrapper.addEventListener("focusin", handleFocusIn, {
        signal: controller.signal,
      });
      wrapper.addEventListener("focusout", handleFocusOut, {
        signal: controller.signal,
      });
    }

    return () => {
      controller.abort();
    };
  }, [
    manualValidation,
    cancelChanges,
    tempRangeValue,
    state.value,
    state.isOpen,
    effectiveWrapperRef,
  ]);

  const segmentRefs = React.useRef<Record<string, React.RefObject<HTMLSpanElement>>>({});

  const [activeSegmentKey, setActiveSegmentKey] = React.useState<string | null>(null);

  const flatSegmentRefs = React.useRef<HTMLSpanElement[]>([]);

  const getOrCreateSegmentRef = (key: string) => {
    if (!segmentRefs.current[key]) {
      segmentRefs.current[key] = React.createRef<HTMLSpanElement>();
    }
    return segmentRefs.current[key];
  };

  const handleSegmentRef =
    (key: string, segmentType: DateSegmentType) => (el: HTMLSpanElement | null) => {
      if (segmentType !== "literal") {
        const existingRef = getOrCreateSegmentRef(key);
        if (existingRef) {
          (existingRef as React.MutableRefObject<HTMLSpanElement | null>).current = el;
        }

        if (el && !flatSegmentRefs.current.includes(el)) {
          flatSegmentRefs.current.push(el);
        }
      }
    };

  // Detect keyboard edit on the date fields
  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === "ArrowRight" || e.key === "ArrowLeft" || e.key === "Tab") {
        // navigation or validation, don't mark as edit
      } else {
        setHasEditedWithKeyboard(true);
      }
      if (e.key === "Enter") {
        e.stopPropagation();
        e.preventDefault();
        if (manualValidation) {
          ignoreNextBlurRef.current = true;
          handleValidation();
        }
      }
      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        const allRefs = flatSegmentRefs.current;
        const currentIndex = allRefs.findIndex(el => el === document.activeElement);
        if (currentIndex >= 0) {
          const nextIndex =
            e.key === "ArrowRight"
              ? Math.min(currentIndex + 1, allRefs.length - 1)
              : Math.max(currentIndex - 1, 0);
          const next = allRefs[nextIndex];
          next?.focus();
          e.preventDefault();
        }
      }
    },
    [handleValidation, manualValidation, setHasEditedWithKeyboard],
  );

  // Reset keyboard edit flag on blur or when value changes from outside
  React.useEffect(() => {
    if (!isFocused) {
      setHasEditedWithKeyboard(false);
    }
  }, [isFocused, setHasEditedWithKeyboard]);
  React.useEffect(() => {
    setHasEditedWithKeyboard(false);
  }, [state.value?.start, state.value?.end, setHasEditedWithKeyboard]);

  // Focus the last segment instead of jumping to the next button and not show the error icon
  React.useEffect(() => {
    const lastSegment = endState.segments.find(s => s.type === "year");
    const key = "end-year";

    if (
      lastSegment &&
      !lastSegment.isPlaceholder &&
      /^\d{4}$/.test(lastSegment.text) &&
      activeSegmentKey === key
    ) {
      const ref = segmentRefs.current[key];
      if (ref?.current && document.activeElement !== ref.current) {
        ref.current.focus();
      }
    }
  }, [endState.segments, activeSegmentKey]);

  const hasSomeValue = React.useMemo(
    () =>
      startState.segments.some(segment => segment.isEditable && !segment.isPlaceholder) ||
      endState.segments.some(segment => segment.isEditable && !segment.isPlaceholder),
    [endState.segments, startState.segments],
  );

  const afterInputSlot = (
    <AfterInputButtonWrapper className={fieldProps.disabled ? "disabled" : ""}>
      {manualValidation && (
        <div
          className={`${validationWrapper} ${isFocused && hasEditedWithKeyboard ? "visible" : ""}`}
        >
          <TooltipV2
            overlay={isValidDate ? t("daterangepicker.tooltip.apply") : error}
            placement="top"
          >
            <div>
              {isValidDate && (
                <ButtonIconV2
                  // to prevent the blur on the DateFieldWrapper
                  onMouseDown={e => {
                    e.preventDefault();
                    ignoreNextBlurRef.current = true;
                  }}
                  onClick={handleValidation}
                >
                  <CheckLine size="m" color={color.lightNitrogen500_Main} />
                </ButtonIconV2>
              )}
              {!isValidDate && (
                <div className={aligned}>
                  <ErrorWarningFill size="m" height="20px" color={colors.oxygen400} />
                </div>
              )}
            </div>
          </TooltipV2>
        </div>
      )}
      {hasSomeValue && !hideCalendar && (
        <TooltipV2 overlay={t("daterangepicker.tooltip.clear")} placement="top">
          <div>
            <ButtonIconV2
              onClick={handleClear}
              onMouseDown={() => {
                ignoreNextBlurRef.current = true;
              }}
            >
              <CloseCircleFill size="m" />
            </ButtonIconV2>
          </div>
        </TooltipV2>
      )}
      {fieldProps.afterInputSlot}
    </AfterInputButtonWrapper>
  );

  return (
    <div className={dateRangeField} ref={effectiveWrapperRef}>
      <StyledField
        {...fieldProps}
        inputContainerClassName={!isValidDate && error !== null ? "error" : ""}
        afterInputSlot={afterInputSlot}
        className={state.isOpen ? "opened" : ""}
      >
        <DateFieldWrapper
          className={cx("date-field-wrapper", {
            disabled: startState.isDisabled || endState.isDisabled,
            calendarHidden: hideCalendar,
          })}
          onKeyDownCapture={handleKeyDown}
        >
          {preset && (
            <Text
              type="body1"
              className={cx(dateRangeFieldPresetDisplay, {
                disabled: fieldProps.disabled,
              })}
            >
              {preset.label}
            </Text>
          )}
          <SegmentWrapper {...startAriaFieldProps} ref={startFieldRef} className="segment-wrapper">
            {startState.segments.map((segment, i) => {
              const key = `start-${segment.type}`;
              return (
                <DateSegment
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  segment={segment}
                  state={startState}
                  forwardedRef={handleSegmentRef(key, segment.type)}
                  onFocus={() => setActiveSegmentKey(key)}
                />
              );
            })}
          </SegmentWrapper>
          {" - "}
          <SegmentWrapper {...endAriaFieldProps} ref={endFieldRef} className="segment-wrapper">
            {endState.segments.map((segment, i) => {
              const key = `end-${segment.type}`;
              return (
                <DateSegment
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                  segment={segment}
                  state={endState}
                  forwardedRef={handleSegmentRef(key, segment.type)}
                  onFocus={() => setActiveSegmentKey(key)}
                />
              );
            })}
          </SegmentWrapper>
        </DateFieldWrapper>
      </StyledField>
      {manualValidation && isFocused && hasEditedWithKeyboard && isValidDate && (
        <div className={instruction}>{t("daterangepicker.instruction")}</div>
      )}
    </div>
  );
}

export default DateRangeField;

const instruction = css`
  position: absolute;
  right: 0;
  color: ${colors.carbon700};
  font-size: 12px;
  margin-top: 4px;
`;

const validationWrapper = css`
  visibility: hidden;

  &.visible {
    visibility: visible;
  }
`;

const dateRangeField = css`
  position: relative;
`;

const dateRangeFieldPresetDisplay = css`
  background: ${colors.hydrogen};
  bottom: 2px;
  left: 0;
  padding: inherit;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 2px;

  &.disabled {
    background: ${colors.titanium50};
  }
`;

const StyledField = styled(Field)`
  width: 100%;

  &:focus-within,
  &.opened {
    .${dateRangeFieldPresetDisplay} {
      display: none;
    }
  }
`;

const DateFieldWrapper = styled.div`
  position: relative;
  display: flex;
  padding: 7px 0px;
  padding-right: 12px;
  gap: 4px;
  flex-grow: 1;

  &.disabled:not(.calendarHidden) {
    opacity: 0.5;
  }

  .InputContainer--disabled & {
    color: ${colors.carbon500};
  }
`;

const SegmentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;

  &.hidden {
    display: none;
  }
`;

const AfterInputButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  margin-right: 8px;

  &.disabled {
    opacity: 0.5;
  }
`;

const aligned = css`
  display: flex;
  align-items: center;
`;
